/**
 * The public app's browser logger — the app-side binding of the SHARED seam.
 *
 * Round-6 L8: the implementation (buffering, dedup, PII scrub, sendBeacon
 * transport, tab-lifecycle flush) moved to
 * `@letsgethome/lib/telemetry/client-logger` so the staff app consumes the
 * same one instead of growing a second copy to drift. What stays here is the
 * only genuinely public-specific part: the closed `component` vocabulary.
 *
 * This module keeps its path and its `clientLogger` export, so every call site
 * is unchanged.
 *
 * @example
 *   import { clientLogger } from '@/shared/lib/client-logger';
 *
 *   clientLogger.error({ error, component: 'GoogleSignIn' }, 'OAuth popup failed');
 *   clientLogger.warn({ component: 'realtime' }, 'WebSocket reconnect failed');
 */

import type { WebBrowserErrorsComponent } from '@/shared/lib/metrics/client-metrics_gen';
import {
  createClientLogger,
  type ClientLogContext,
} from '@letsgethome/lib/telemetry/client-logger';

/**
 * The originating UI component/subsystem of a transmitted log. Required on
 * every `error`/`warn` (a compile error to omit) so the ingest can promote it
 * to the `web_browser_errors{component}` counter. The vocabulary is the closed
 * SSoT set in `web/manifests/metrics.yaml` (generated as
 * `WebBrowserErrorsComponent`) — which is exactly why the shared seam takes it
 * as a type parameter rather than declaring one: `lib` sits below `apps` in the
 * package DAG and cannot see this generated type.
 */
export type WebLogComponent = WebBrowserErrorsComponent;

/** Context for a transmitted (`error`/`warn`) log — `component` is required. */
export type WebLogContext = ClientLogContext<WebLogComponent>;

export const clientLogger = createClientLogger<WebLogComponent>();
